import { useState } from 'react';
import { Badge, Text, Tooltip, Group, Box, ActionIcon, Modal, Stack, Divider } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { Maximize2, StickyNote } from 'lucide-react';
import type { DataLibrarySessionRow, DataLibrarySessionAttendee, DataLibrarySessionNote, DataLibrarySessionAirtimeEntry } from '../../../types';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { AttendanceRate, GroupBadge } from '../../common';
import { getAttendanceCoverageDisplay } from '../../../utils/attendanceCoverage';
import { RichTextContent } from '../../common/RichTextEditor/RichTextContent';

/**
 * Canonical attendance pill colors — use these app-wide
 * whenever a color needs to be associated with attendance.
 */
export const ATTENDANCE_PILL_CONFIG: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  attended: { bg: '#4A7C59', text: '#fff', label: 'Attended' },
  partial: { bg: '#4A7C59', text: '#fff', label: 'Partially Attended' },
  excused_business: { bg: '#F9A825', text: '#fff', label: 'Excused Absence' },
  excused_personal: { bg: '#F9A825', text: '#fff', label: 'Excused Absence' },
  unexcused: { bg: '#D64545', text: '#fff', label: 'Unexcused Absence' },
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getISOWeekMonday(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(d);
  monday.setUTCDate(diff);
  return monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getSessionMonth(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function AttendancePill({ attendee }: { attendee: DataLibrarySessionAttendee }) {
  const status = attendee.status;
  if (!status) {
    return (
      <Tooltip label={`${attendee.participant_name} — Not Marked`} withArrow>
        <Badge
          size="sm"
          color="gray"
          variant="light"
          styles={{ label: { fontSize: 11, letterSpacing: 0.3, textTransform: 'none' } }}
        >
          Not Marked
        </Badge>
      </Tooltip>
    );
  }

  const cfg = ATTENDANCE_PILL_CONFIG[status];
  if (!cfg) return null;

  return (
    <Tooltip label={`${attendee.participant_name} — ${cfg.label}`} withArrow>
      <Badge
        size="sm"
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
        }}
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
}

const ENGAGEMENT_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  5: { bg: '#28463A', text: '#fff', label: '5 — Highly Engaged' },
  4: { bg: '#4A7C59', text: '#fff', label: '4 — Engaged' },
  3: { bg: '#E9A23B', text: '#fff', label: '3 — Moderate' },
  2: { bg: '#E07B4C', text: '#fff', label: '2 — Low' },
  1: { bg: '#D64545', text: '#fff', label: '1 — Disengaged' },
};

function EngagementPill({ entry }: { entry: { participant_name: string; rating: number } }) {
  const cfg = ENGAGEMENT_COLORS[entry.rating] || { bg: '#888', text: '#fff', label: String(entry.rating) };
  return (
    <Tooltip label={`${entry.participant_name} — ${cfg.label}`} withArrow>
      <Badge
        size="sm"
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
        }}
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
}

function EngagementPillList({ engagement }: { engagement: Array<{ participant_name: string; rating: number }> }) {
  if (!engagement || engagement.length === 0) {
    return <Text size="xs" c="dimmed" fs="italic">No ratings</Text>;
  }

  return (
    <Group gap={4} wrap="wrap">
      {engagement.map((e, i) => (
        <EngagementPill key={i} entry={e} />
      ))}
    </Group>
  );
}

/**
 * MED-1200 (C2C) — airtime pill colors. Binary by the shared one-participant-
 * dominating rule (AC3): dangerRed when this participant dominates the session's
 * participant talk-time (per B10's persisted verdict), medleyGreen otherwise.
 * No middle/orange band and no under-participation flag for closeout.
 */
const AIRTIME_DOMINATES_COLOR = { bg: '#D64545', text: '#fff' }; // dangerRed
const AIRTIME_BALANCED_COLOR = { bg: '#4A7C59', text: '#fff' }; // medleyGreen

function AirtimePill({ entry }: { entry: DataLibrarySessionAirtimeEntry }) {
  // MED-1254 (Group A / AC4) — a present-but-silent attendee (0% share) gets a
  // neutral gray treatment matching the attendance "Not Marked" badge. Never
  // green (implies balanced participation) and never red (dominating). This is a
  // no-data state for a non-speaker, not an under-participation flag.
  if (entry.share_of_participants_pct === 0) {
    return (
      <Tooltip label={`${entry.label} — 0% of speaking time`} withArrow>
        <Badge
          size="sm"
          color="gray"
          variant="light"
          styles={{ label: { fontSize: 11, letterSpacing: 0.3, textTransform: 'none' } }}
        >
          0%
        </Badge>
      </Tooltip>
    );
  }

  const cfg = entry.dominates ? AIRTIME_DOMINATES_COLOR : AIRTIME_BALANCED_COLOR;
  // Tooltip uses the anonymized "Participant N" label (AC2) — never the real
  // name, even though the adjacent Engagement/Participants columns show identities.
  return (
    <Tooltip label={`${entry.label} — ${entry.share_of_participants_pct}% of speaking time`} withArrow>
      <Badge
        size="sm"
        styles={{
          root: { backgroundColor: cfg.bg },
          label: { color: cfg.text, fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
        }}
      >
        {entry.share_of_participants_pct}%
      </Badge>
    </Tooltip>
  );
}

/**
 * MED-1200 (C2C) — per-participant airtime cell, modeled on EngagementPillList.
 * Renders the tri-state carried by DataLibrarySessionRow.airtime:
 *   null → cohort not LSD + group-coaching → "—" (AC6, not applicable)
 *   []   → qualifying cohort, no usable transcript → "Not available" (AC5)
 *   [..] → one pill per participant, ranked by share descending (MED-1254 / AC6)
 *
 * MED-1254 (Group B) — pills sort by share_of_participants_pct descending (AC6)
 * so a CM reads who dominated → who was quiet left-to-right. Ties break on the
 * numeric "Participant N" label for a deterministic, stable order across renders
 * (AC7). Silent 0% attendees (Group A) naturally fall to the end (AC8). This is a
 * display-side sort on a copy — the stored B6 distribution is untouched.
 */
function AirtimePillList({ airtime }: { airtime: DataLibrarySessionAirtimeEntry[] | null }) {
  if (airtime === null) {
    return <Text size="xs" c="dimmed">—</Text>;
  }
  if (airtime.length === 0) {
    return <Text size="xs" c="dimmed" fs="italic">Not available</Text>;
  }
  const sorted = [...airtime].sort(
    (a, b) =>
      b.share_of_participants_pct - a.share_of_participants_pct ||
      a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  return (
    <Group gap={4} wrap="wrap">
      {sorted.map((e, i) => (
        <AirtimePill key={i} entry={e} />
      ))}
    </Group>
  );
}

function AttendancePillList({ attendance }: { attendance: DataLibrarySessionAttendee[] }) {
  if (attendance.length === 0) {
    return <Text size="xs" c="dimmed" fs="italic">No participants</Text>;
  }

  return (
    <Group gap={4} wrap="wrap">
      {attendance.map((a, i) => (
        <AttendancePill key={i} attendee={a} />
      ))}
    </Group>
  );
}

function ParticipantEmailList({ attendance, cohortId }: { attendance: DataLibrarySessionAttendee[]; cohortId: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useClickOutside(() => setIsExpanded(false));

  if (attendance.length === 0) {
    return <Text size="xs" c="dimmed">—</Text>;
  }

  // Collapsed: first click expands the cell (AC8), pills are not individually clickable
  if (!isExpanded) {
    return (
      <Box
        onClick={() => setIsExpanded(true)}
        style={{
          cursor: 'pointer',
          overflow: 'hidden',
          maxHeight: 48,
        }}
      >
        <Group gap={4} wrap="wrap">
          {attendance.map((a, i) => (
            <Badge
              key={i}
              size="sm"
              variant="light"
              color="gray"
              styles={{ label: { fontSize: 11, textTransform: 'none', letterSpacing: 0.3 } }}
            >
              {a.participant_email}
            </Badge>
          ))}
        </Group>
      </Box>
    );
  }

  // Expanded: individual pills are clickable to open participant modal (AC9)
  return (
    <>
      <Box
        ref={ref}
        style={{
          position: 'relative',
          outline: '2px solid #228BE6',
          outlineOffset: 1,
          borderRadius: 4,
          padding: '4px 32px 4px 6px',
          backgroundColor: '#fff',
          zIndex: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Group gap={4} wrap="wrap">
          {attendance.map((a, i) => (
            a.participant_id ? (
              <ParticipantEmailPill
                key={i}
                email={a.participant_email}
                participantId={a.participant_id}
                cohortId={cohortId}
              />
            ) : (
              <Badge
                key={i}
                size="sm"
                variant="light"
                color="gray"
                styles={{ label: { fontSize: 11, textTransform: 'none', letterSpacing: 0.3 } }}
              >
                {a.participant_email}
              </Badge>
            )
          ))}
        </Group>
        <Tooltip label="Expand cell" withArrow position="top">
          <ActionIcon
            size="sm"
            variant="filled"
            color="blue"
            style={{ position: 'absolute', top: 4, right: 4 }}
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
          >
            <Maximize2 size={12} />
          </ActionIcon>
        </Tooltip>
      </Box>
      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsExpanded(false);
        }}
        title="Participants"
        size="lg"
        withinPortal
      >
        <Group gap={6} wrap="wrap">
          {attendance.map((a, i) => (
            a.participant_id ? (
              <ParticipantEmailPill
                key={i}
                email={a.participant_email}
                participantId={a.participant_id}
                cohortId={cohortId}
              />
            ) : (
              <Badge
                key={i}
                size="md"
                variant="light"
                color="gray"
                styles={{ label: { fontSize: 12, textTransform: 'none', letterSpacing: 0.3 } }}
              >
                {a.participant_email}
              </Badge>
            )
          ))}
        </Group>
      </Modal>
    </>
  );
}

const SESSION_STATUS_PILL: Record<string, { label: string; bg: string; text: string }> = {
  completed: { label: 'Completed', bg: '#4A7C59', text: '#fff' },
  in_progress: { label: 'In Progress', bg: '#E9A23B', text: '#fff' },
  scheduled: { label: 'Scheduled', bg: '#E9A23B', text: '#fff' },
  unscheduled: { label: 'Unscheduled', bg: '#9AA5A0', text: '#fff' },
  cancelled: { label: 'Cancelled', bg: '#9AA5A0', text: '#fff' },
  rescheduled: { label: 'Rescheduled', bg: '#9AA5A0', text: '#fff' },
  // MED-1007 (E4.0g · AC4): terminal no-show. Distinct dangerRed so it reads
  // apart from the gray cancelled/rescheduled pills in session listings.
  missed: { label: 'Missed', bg: '#D64545', text: '#fff' },
};

function SessionStatusPill({ status }: { status: string }) {
  const cfg = SESSION_STATUS_PILL[status];
  if (!cfg) {
    return <Text size="sm" c="dimmed">—</Text>;
  }
  return (
    <Badge
      size="sm"
      styles={{
        root: { backgroundColor: cfg.bg },
        label: {
          color: cfg.text,
          fontSize: 10,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        },
      }}
    >
      {cfg.label}
    </Badge>
  );
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
}

function formatNoteDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function InternalNotesCell({ notes }: { notes: DataLibrarySessionNote[] }) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!notes || notes.length === 0) {
    return <Text size="sm" c="dimmed">&mdash;</Text>;
  }

  const latest = notes[0]; // already sorted DESC from API
  const plainText = stripHtml(latest.content);
  const preview = plainText.length > 150 ? plainText.slice(0, 150) + '...' : plainText;

  return (
    <>
      <Box
        onClick={() => setModalOpen(true)}
        style={{ cursor: 'pointer', maxWidth: 380 }}
      >
        <Group gap={6} wrap="nowrap" align="flex-start">
          <StickyNote size={14} color="#888" style={{ flexShrink: 0, marginTop: 2 }} />
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" lineClamp={3} style={{ textOverflow: 'ellipsis' }}>
              {notes.length > 1 && (
                <Text span fw={600} size="sm">{notes.length} notes — </Text>
              )}
              {preview}
            </Text>
            <Text size="xs" c="blue" mt={2}>View{notes.length > 1 ? ' all' : ''} →</Text>
          </Box>
        </Group>
      </Box>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Internal Notes"
        size="lg"
        withinPortal
      >
        <Stack gap="md">
          {notes.map((note, i) => (
            <Box key={note.id}>
              {i > 0 && <Divider mb="md" />}
              <Group gap="xs" mb={4}>
                <Text size="xs" fw={600}>{note.author_name}</Text>
                <Text size="xs" c="dimmed">{formatNoteDate(note.created_at)}</Text>
              </Group>
              <Box style={{ fontSize: 14, lineHeight: 1.5 }}>
                <RichTextContent content={note.content} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Modal>
    </>
  );
}

function GroupSessionCell({ row }: { row: DataLibrarySessionRow }) {
  const sessionLabel = row.session_number != null ? `Session ${row.session_number}` : null;

  if (!row.group_name || !row.group_id) {
    return (
      <Text size="sm" fw={500}>
        {['Unassigned', sessionLabel].filter(Boolean).join(' — ')}
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap" align="center">
      <GroupBadge groupName={row.group_name} groupId={row.group_id} size="sm" />
      {sessionLabel && (
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          — {sessionLabel}
        </Text>
      )}
    </Group>
  );
}

export function renderSessionCell(
  row: DataLibrarySessionRow,
  columnId: string,
): React.ReactNode {
  switch (columnId) {
    case 'group_session':
      return <GroupSessionCell row={row} />;
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(row.cohort_health);
    case 'session_number':
      return <Text size="sm">{row.session_number ?? '—'}</Text>;
    case 'session_topic':
      return <Text size="sm">{row.session_topic || '—'}</Text>;
    case 'session_status':
      return <SessionStatusPill status={row.session_status} />;
    case 'health_nudges':
      if (!row.health_nudges || row.health_nudges.length === 0) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      return (
        <Group gap={4} wrap="wrap">
          {row.health_nudges.map((nudge, i) => (
            <Tooltip key={i} label={nudge.title} withArrow>
              <Badge
                size="xs"
                color={nudge.severity === 'risk' ? 'red' : 'orange'}
                variant="light"
                styles={{ label: { textTransform: 'none' } }}
              >
                {nudge.stat_label} {nudge.stat_value}
              </Badge>
            </Tooltip>
          ))}
        </Group>
      );
    case 'group_name':
      return <GroupBadge groupName={row.group_name} groupId={row.group_id} />;
    case 'coach_name':
      return <Text size="sm">{row.coach_name || '—'}</Text>;
    // MED-1266: rate carries marking coverage via the shared component (nullable rate, AC10/AC13).
    case 'session_attendance_rate':
      return (
        <AttendanceRate
          rate={row.session_attendance_rate}
          marked={row.session_attendance_marked}
          population={row.session_attendance_population}
          variant="compact"
          size="sm"
        />
      );
    // MED-1266 (AC12): marking coverage as its own column.
    case 'session_attendance_coverage': {
      const d = getAttendanceCoverageDisplay({
        rate: 1,
        marked: row.session_attendance_marked,
        population: row.session_attendance_population,
      });
      return <Text size="sm">{d.isUnrecorded ? '—' : d.isComplete ? 'Complete' : d.coverageLabel}</Text>;
    }
    case 'attendance':
      return <AttendancePillList attendance={row.attendance} />;
    case 'engagement':
      return <EngagementPillList engagement={row.engagement || []} />;
    case 'airtime':
      // Pass the tri-state through verbatim (?? null, never || []) so the cell
      // keeps AC5 ("Not available") distinct from AC6 ("—", not applicable).
      return <AirtimePillList airtime={row.airtime ?? null} />;
    case 'session_date':
      return (
        <Text size="sm" c="dimmed">
          {row.session_date ? formatDate(row.session_date) : '—'}
        </Text>
      );
    case 'session_week':
      return (
        <Text size="sm" c="dimmed">
          {row.session_date ? getISOWeekMonday(row.session_date) : '—'}
        </Text>
      );
    case 'session_month':
      return (
        <Text size="sm" c="dimmed">
          {row.session_date ? getSessionMonth(row.session_date) : '—'}
        </Text>
      );
    case 'participants':
      return <ParticipantEmailList attendance={row.attendance} cohortId={row.cohort_id} />;
    case 'total_attended':
      return <Text size="sm">{row.total_attended}</Text>;
    case 'total_participants':
      return <Text size="sm">{row.total_participants}</Text>;
    case 'internal_notes':
      return <InternalNotesCell notes={row.internal_notes} />;
    default:
      return <Text size="sm">—</Text>;
  }
}

// Export helpers for group-by key derivation
export { getISOWeekMonday, getSessionMonth };
